import { createSelector } from "@reduxjs/toolkit";

import { selectCurrentUserId } from "../local";

import { selectUserIdsWithCapability } from "./capability";
import { selectClassInfo } from "./info";
import { selectResources } from "./resource";

import { CapabilityType, RootState } from "shared/types";

export const selectActiveBreakoutSessionId = () => (state: RootState) =>
  state.classroom.client.active_breakout_session_id;

export const selectActiveBreakoutSession = () =>
  createSelector(
    [selectActiveBreakoutSessionId(), selectBreakoutSessions()],
    (activeBreakoutSessionId, _breakoutSessions) =>
      activeBreakoutSessionId ? _breakoutSessions[activeBreakoutSessionId] : null,
  );

export const selectActiveBreakout = () =>
  createSelector(
    [selectActiveBreakoutSession(), selectBreakouts()],
    (activeBreakoutSession, breakouts) =>
      activeBreakoutSession ? breakouts[activeBreakoutSession.breakout_id] : null,
  );

export const selectBreakoutById = (id: string) => (state: RootState) =>
  state.classroom.client.breakouts[id];

export const selectBreakouts = () => (state: RootState) => state.classroom.client.breakouts;

export const selectBreakoutRooms = () => (state: RootState) =>
  state.classroom.client.breakout_rooms;

export const selectBreakoutRoomsByBreakoutId = (breakoutId: string) =>
  createSelector([selectBreakoutRooms(), () => breakoutId], (breakoutRooms, _breakoutId) =>
    Object.values(breakoutRooms).filter((breakoutRoom) => breakoutRoom.breakout_id === _breakoutId),
  );

export const selectBreakoutRoomsForActiveBreakout = () =>
  createSelector(
    [selectActiveBreakoutSession(), selectBreakoutRooms()],
    (activeBreakoutSession, breakoutRooms) => {
      if (!activeBreakoutSession?.breakout_id) {
        return [];
      }
      return Object.values(breakoutRooms).filter(
        (breakoutRoom) => breakoutRoom.breakout_id === activeBreakoutSession.breakout_id,
      );
    },
  );

export const selectAssignedBreakoutRoomForCurrentUser = () =>
  createSelector(
    [selectActiveBreakoutSession(), selectCurrentUserId(), selectBreakoutRooms()],
    (activeBreakoutSession, userId, breakoutRooms) => {
      if (!activeBreakoutSession?.breakout_id) {
        return null;
      }
      return (
        Object.values(breakoutRooms).find(
          (breakoutRoom) =>
            breakoutRoom.breakout_id === activeBreakoutSession.breakout_id &&
            breakoutRoom.assigned_user_ids.includes(userId),
        ) || null
      );
    },
  );

export const selectAssignedBreakoutRoomByUserId = (userId: string) =>
  createSelector(
    [selectActiveBreakoutSession(), selectBreakoutRooms()],
    (activeBreakoutSession, breakoutRooms) => {
      if (!activeBreakoutSession?.breakout_id) {
        return null;
      }
      return (
        Object.values(breakoutRooms).find(
          (breakoutRoom) =>
            breakoutRoom.breakout_id === activeBreakoutSession.breakout_id &&
            breakoutRoom.assigned_user_ids.includes(userId),
        ) || null
      );
    },
  );

export const selectVisitingBreakoutRoomForCurrentUser = () =>
  createSelector(
    [selectActiveBreakoutSession(), selectCurrentUserId(), selectBreakoutRooms()],
    (activeBreakoutSession, userId, breakoutRooms) => {
      if (!activeBreakoutSession?.breakout_id) {
        return null;
      }
      return (
        Object.values(breakoutRooms).find(
          (breakoutRoom) =>
            breakoutRoom.breakout_id === activeBreakoutSession.breakout_id &&
            breakoutRoom.visiting_user_ids.includes(userId),
        ) || null
      );
    },
  );

export const selectVisitingBreakoutRoomByUserId = (userId: string) =>
  createSelector(
    [selectActiveBreakoutSession(), selectBreakoutRooms()],
    (activeBreakoutSession, breakoutRooms) => {
      if (!activeBreakoutSession?.breakout_id) {
        return null;
      }
      return (
        Object.values(breakoutRooms).find(
          (breakoutRoom) =>
            breakoutRoom.breakout_id === activeBreakoutSession.breakout_id &&
            breakoutRoom.visiting_user_ids.includes(userId),
        ) || null
      );
    },
  );

export const selectListeningBreakoutRoomForCurrentUser = () =>
  createSelector(
    [selectActiveBreakoutSession(), selectCurrentUserId(), selectBreakoutRooms()],
    (activeBreakoutSession, userId, breakoutRooms) => {
      if (!activeBreakoutSession?.breakout_id) {
        return null;
      }
      return (
        Object.values(breakoutRooms).find(
          (breakoutRoom) =>
            breakoutRoom.breakout_id === activeBreakoutSession.breakout_id &&
            breakoutRoom.listening_user_ids.includes(userId),
        ) || null
      );
    },
  );

export const selectActiveBreakoutRoomForCurrentUser = () =>
  createSelector(
    [selectAssignedBreakoutRoomForCurrentUser(), selectVisitingBreakoutRoomForCurrentUser()],
    (assignedBreakoutRoom, visitingBreakoutRoom) => assignedBreakoutRoom || visitingBreakoutRoom,
  );

export const selectActiveBreakoutRoomForUserId = (userId: string | null | undefined) =>
  userId
    ? createSelector(
        [selectAssignedBreakoutRoomByUserId(userId), selectVisitingBreakoutRoomByUserId(userId)],
        (assignedBreakoutRoom, visitingBreakoutRoom) =>
          assignedBreakoutRoom || visitingBreakoutRoom,
      )
    : () => null;

// Returns breakout notes for the active breakout, if applicable. Otherwise, returns breakout notes for all past breakouts.
export const selectBreakoutNotesForCurrentUser = () =>
  createSelector(
    [
      selectActiveBreakoutSession(),
      selectBreakoutSessions(),
      selectCurrentUserId(),
      selectBreakoutRooms(),
      selectResources(),
    ],
    (activeBreakoutSession, breakoutSessions, userId, breakoutRooms, resources) => {
      if (activeBreakoutSession) {
        const breakoutRoom = Object.values(breakoutRooms).find(
          (_breakoutRoom) =>
            _breakoutRoom.breakout_id === activeBreakoutSession.breakout_id &&
            _breakoutRoom.assigned_user_ids.includes(userId),
        );
        if (!breakoutRoom) return [];
        return Object.values(resources).filter(
          (resource) =>
            breakoutRoom.resource_ids.includes(resource.id) && resource.is_breakout_group_notes,
        );
      }
      const pastBreakoutIds = Object.values(breakoutSessions).map(
        (breakoutSession) => breakoutSession.breakout_id,
      );
      const pastBreakoutRooms = Object.values(breakoutRooms).filter(
        (breakoutRoom) =>
          pastBreakoutIds.includes(breakoutRoom.breakout_id) &&
          breakoutRoom.assigned_user_ids.includes(userId),
      );
      const breakoutRoomResourceIds = pastBreakoutRooms
        .map((breakoutRoom) => breakoutRoom.resource_ids)
        .flat();
      return Object.values(resources).filter(
        (resource) =>
          breakoutRoomResourceIds.includes(resource.id) && resource.is_breakout_group_notes,
      );
    },
  );

export const selectBreakoutSessionById = (id: string) => (state: RootState) =>
  state.classroom.client.breakout_sessions[id];

export const selectBreakoutSessions = () => (state: RootState) =>
  state.classroom.client.breakout_sessions;

export const selectBreakoutSessionsByBreakoutId = (breakoutId: string) =>
  createSelector([selectBreakoutSessions(), () => breakoutId], (breakoutSessions, _breakoutId) =>
    Object.values(breakoutSessions).filter(
      (breakoutSession) => breakoutSession.breakout_id === _breakoutId,
    ),
  );

export const selectActiveRoomIdForCurrentUser = () =>
  createSelector(
    [selectActiveBreakoutRoomForCurrentUser(), selectClassInfo()],
    (activeBreakoutRoom, classInfo) =>
      activeBreakoutRoom ? activeBreakoutRoom.room_id : classInfo.room_id,
  );

export const selectCurrentUserIsInBreakoutControlRoom = () =>
  createSelector(
    [
      selectCurrentUserId(),
      selectActiveBreakoutSessionId(),
      selectActiveBreakoutRoomForCurrentUser(),
      selectUserIdsWithCapability(CapabilityType.CAN_PARTICIPATE_IN_CLASS),
    ],
    (currentUserId, activeBreakoutSessionId, activeBreakoutRoom, canParticipateInClassUserIds) =>
      !!(
        activeBreakoutSessionId &&
        !activeBreakoutRoom &&
        !canParticipateInClassUserIds.includes(currentUserId)
      ),
  );
